import { useEffect, useState } from 'react'
import { useMeProfileCheckProfileSlug } from '@cocoonspace/api'
import { useLingui } from '@lingui/react/macro'
import { CheckIcon, LoaderCircleIcon, XIcon } from 'lucide-react'
import slugify from 'slugify'
import { useDebounce } from 'use-debounce'

import {
	filterAvailableProfileSlugs,
	isProfileSlugAvailable,
} from '../reserved-profile-slugs'

const StatusIcon = ({
	isLoading,
	isValid,
	isError,
}: {
	isLoading: boolean
	isValid: boolean
	isError: boolean
}) => (
	<>
		{isLoading && (
			<LoaderCircleIcon className='text-muted-foreground ml-2 size-4 animate-spin' />
		)}

		{isValid && <CheckIcon className='ml-2 size-4 text-emerald-500' />}
		{isError && <XIcon className='text-destructive ml-2 size-4' />}
	</>
)

export const SlugInput = ({
	value,
	onChange,
}: {
	value: string
	onChange: (value: string) => void
}) => {
	const { t } = useLingui()

	const [slug, setSlug] = useState(value ?? '')
	const [debouncedSlug] = useDebounce<string>(slug, 500)

	const {
		data: { isAvailable: isAvailableFromApi, alternatives } = {},
		isLoading,
	} = useMeProfileCheckProfileSlug({ slug: debouncedSlug })
	const isAvailable =
		isAvailableFromApi && isProfileSlugAvailable(debouncedSlug)

	useEffect(() => {
		setSlug(value ?? '')
	}, [value])

	useEffect(() => {
		if (isLoading || !isAvailable || slug === debouncedSlug) return

		onChange(slug)
	}, [debouncedSlug, onChange, isLoading, isAvailable, slug])

	const isvalid = !isLoading && !!isAvailable && !!slug

	return (
		<div>
			<div className='flex h-[41px] items-center overflow-hidden rounded-md border'>
				<div className='text-muted-foreground bg-slate-100 p-4 pr-2 text-sm'>
					https://www.cocoon.space/
				</div>

				<div className='flex flex-1 items-center pr-2'>
					<input
						value={slug}
						placeholder={t`Ex: john-doe`}
						className='w-full p-4 pl-2 text-sm !ring-0 outline-none'
						inputMode='text'
						autoCapitalize='none'
						autoCorrect='off'
						spellCheck={false}
						onChange={(e) => {
							const normalized = slugify(e.target.value, {
								lower: true,
								strict: true,
								trim: false,
							})

							setSlug(normalized)
						}}
					/>

					<StatusIcon
						isLoading={isLoading}
						isValid={isvalid}
						isError={!isvalid}
					/>
				</div>
			</div>

			{!isvalid && (
				<div className='flex w-full flex-col pt-1 text-left text-sm'>
					<div className='text-muted-foreground text-sm'>Alternatives:</div>

					<div className='flex flex-wrap gap-2'>
						{filterAvailableProfileSlugs(alternatives ?? [])
							.slice(0, 2)
							.map((alternative) => (
								<button
									key={alternative}
									type='button'
									onClick={() => setSlug(alternative)}
									className='text-muted-foreground hover:text-foreground text-left text-sm underline'
								>
									{alternative}
								</button>
							))}
					</div>
				</div>
			)}
		</div>
	)
}
